/**
 * Hands a "start a thread with this message" request from a button outside
 * the thread sidebar (such as "Build with chat" on the agents list, or Ask AI
 * on a board) to the sidebar.
 *
 * `stashPendingKickoff` writes the kickoff to sessionStorage and dispatches
 * `PENDING_KICKOFF_EVENT`. `subscribePendingKickoff` reads both: on subscribe
 * it takes any kickoff stashed before the sidebar mounted, and while
 * subscribed it takes each event. Both reach one handler, so callers need not
 * dedupe; the stored copy exists only for a sidebar that mounts after the
 * stash and so missed the event.
 *
 * Two quick `stashPendingKickoff` calls dispatch two synchronous events, which
 * a mounted subscriber runs one after the other without merging them. A caller
 * that needs them merged merges them itself: by the time the second stash
 * lands, the first thread has started, and dropping a repeat here would race
 * that start.
 */

const PENDING_KICKOFF_STORAGE_KEY = "roboto.pending-agentThread-kickoff";

/** Custom DOM event dispatched on the window when a kickoff is stashed. */
export const PENDING_KICKOFF_EVENT = "roboto:pending-agentThread-kickoff";

/**
 * The first message of a thread a button starts. `message` is what the
 * thread shows as the person's message, in the person's words.
 * `requestDetails` is what the model needs beside it to act, such as the
 * ids of the board and panel and the tools to use; it travels in the
 * message's viewing context (`client_context.misc_context.request_details`),
 * which the model reads after the message and the thread does not show.
 */
export interface PendingKickoff {
  message: string;
  requestDetails?: string;
}

interface PendingKickoffEventDetail {
  kickoff: PendingKickoff;
}

/**
 * Stashes a kickoff and announces it to any mounted subscriber. Two stashes
 * in quick succession fire two events; see the module docstring.
 *
 * When sessionStorage is unwritable, the storage write is skipped and the
 * event still fires, so a sidebar that is already open still starts the
 * thread; only a sidebar that mounts later misses the kickoff.
 */
export function stashPendingKickoff(kickoff: PendingKickoff): void {
  try {
    sessionStorage.setItem(
      PENDING_KICKOFF_STORAGE_KEY,
      JSON.stringify(kickoff),
    );
  } catch {
    // Storage unavailable: see docstring.
  }
  window.dispatchEvent(
    new CustomEvent<PendingKickoffEventDetail>(PENDING_KICKOFF_EVENT, {
      detail: { kickoff },
    }),
  );
}

/**
 * Reads and clears the pending kickoff, so a second call returns `null` even
 * when the first returned one. Returns `null` when nothing is stashed, when
 * sessionStorage cannot be read, or when the stash is not JSON.
 * `subscribePendingKickoff` drains the stash through it; a component that
 * starts threads subscribes instead, and tests call it to read and clear the
 * stash.
 */
export function consumePendingKickoff(): PendingKickoff | null {
  let value: string | null;
  try {
    value = sessionStorage.getItem(PENDING_KICKOFF_STORAGE_KEY);
  } catch {
    return null;
  }
  if (value === null) {
    return null;
  }
  try {
    sessionStorage.removeItem(PENDING_KICKOFF_STORAGE_KEY);
  } catch {
    // Return the kickoff even when the stash cannot be removed.
  }
  try {
    return JSON.parse(value) as PendingKickoff;
  } catch {
    return null;
  }
}

/**
 * Calls `handler` once with any kickoff stashed before the subscription, then
 * for every `PENDING_KICKOFF_EVENT` the window receives while subscribed. Each
 * event also drains the stash, so a sidebar that subscribes later does not
 * start the same kickoff again.
 *
 * Returns the unsubscribe function, in the shape `React.useEffect` expects
 * from its cleanup.
 *
 * `handler` is called with whatever it closed over when it subscribed. A
 * caller that needs current React state keeps the latest closure in a ref,
 * reassigned after each render, and has `handler` call through the ref.
 */
export function subscribePendingKickoff(
  handler: (kickoff: PendingKickoff) => void,
): () => void {
  const initial = consumePendingKickoff();
  if (initial !== null) {
    handler(initial);
  }
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<PendingKickoffEventDetail>).detail;
    consumePendingKickoff();
    handler(detail.kickoff);
  };
  window.addEventListener(PENDING_KICKOFF_EVENT, listener);
  return () => {
    window.removeEventListener(PENDING_KICKOFF_EVENT, listener);
  };
}
